/**
 * Tripnovac Visa — Premium Pricing Page
 * World-class tier display with Stripe checkout
 * 
 * @company Alpha Libra
 * @domain tripnovac-visa.com
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Grid, Card, CardContent, CardActions,
  Button, Chip, List, ListItem, ListItemIcon, ListItemText,
  Avatar, Paper, Divider, Fade, CircularProgress,
} from '@mui/material';
import {
  Check, Close, Star, Bolt, Business, Apartment, RocketLaunch,
  Support, Security, Person, LocalOffer,
  ArrowForward, Celebration,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { API_BASE_URL, EMAILS } from '../../constants';

// =====================================================
// PRICING TIERS
// =====================================================

const TIERS = [
  {
    id: 'free',
    name: 'Free',
    description: 'Get started with basic visa information',
    monthlyPrice: 0,
    yearlyPrice: 0,
    icon: <Person />,
    color: '#6B7280',
    gradient: 'linear-gradient(135deg, #6B7280 0%, #4B5563 100%)',
    features: [
      { text: 'Browse 249 countries and territories (ISO 3166-1)', included: true },
      { text: 'View visa requirements', included: true },
      { text: '1 application per month', included: true },
      { text: 'Basic email support', included: true },
      { text: 'Interview preparation', included: false },
      { text: 'AI visa advisor', included: false },
      { text: 'Priority processing', included: false },
    ],
    cta: 'Get Started',
    popular: false,
  },
  {
    id: 'premium',
    name: 'Premium',
    description: 'For frequent travelers and professionals',
    monthlyPrice: 19.99,
    yearlyPrice: 149.99,
    icon: <Star />,
    color: '#8B5CF6',
    gradient: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
    features: [
      { text: 'Everything in Free', included: true },
      { text: 'Applications within Premium plan limits', included: true },
      { text: 'Interview preparation', included: true },
      { text: 'AI visa advisor', included: true },
      { text: 'Document checklists', included: true },
      { text: 'Application tracking', included: true },
      { text: 'Priority support', included: true },
    ],
    cta: 'Upgrade Now',
    popular: true,
    savings: 'Save $90/year',
  },
  {
    id: 'agent',
    name: 'Travel Agent',
    description: 'For travel agencies and consultants',
    monthlyPrice: 49.99,
    yearlyPrice: 449.99,
    icon: <Bolt />,
    color: '#F59E0B',
    gradient: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
    features: [
      { text: 'Everything in Premium', included: true },
      { text: 'Client management CRM', included: true },
      { text: 'Bulk application submission', included: true },
      { text: 'Commission tracking', included: true },
      { text: 'White-label options', included: true },
      { text: 'Up to 5 sub-accounts', included: true },
      { text: 'Dedicated support line', included: true },
    ],
    cta: 'Start Free Trial',
    popular: false,
    savings: 'Save $150/year',
  },
  {
    id: 'corporate',
    name: 'Corporate',
    description: 'For businesses managing employee travel',
    monthlyPrice: 199.99,
    yearlyPrice: 1799.99,
    icon: <Business />,
    color: '#10B981',
    gradient: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
    features: [
      { text: 'Everything in Agent', included: true },
      { text: 'Employee management', included: true },
      { text: 'Department organization', included: true },
      { text: 'Approval workflows', included: true },
      { text: 'Expense integration', included: true },
      { text: 'Analytics dashboard', included: true },
      { text: 'Account manager', included: true },
    ],
    cta: 'Contact Sales',
    popular: false,
    savings: 'Save $600/year',
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    description: 'Custom solutions for large organizations',
    monthlyPrice: null,
    yearlyPrice: null,
    icon: <Apartment />,
    color: '#3B82F6',
    gradient: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)',
    features: [
      { text: 'Everything in Corporate', included: true },
      { text: 'Enterprise pilot scoping', included: true },
      { text: 'API and integration discovery', included: true },
      { text: 'Custom workflow assessment', included: true },
      { text: 'Service levels by written agreement', included: true },
      { text: 'Deployment architecture review', included: true },
      { text: 'Support coverage by agreement', included: true },
    ],
    cta: 'Contact Us',
    popular: false,
  },
];

const FEATURE_COMPARISON = [
  { name: 'Countries Access', free: '250', premium: '250', agent: '250', corporate: '250', enterprise: '250' },
  { name: 'Visa Applications', free: '1/month', premium: 'Unlimited', agent: 'Unlimited', corporate: 'Unlimited', enterprise: 'Unlimited' },
  { name: 'Interview Prep', free: false, premium: true, agent: true, corporate: true, enterprise: true },
  { name: 'Voice Coaching', free: false, premium: true, agent: true, corporate: true, enterprise: true },
  { name: 'Document Checklists', free: false, premium: true, agent: true, corporate: true, enterprise: true },
  { name: 'Client Management', free: false, premium: false, agent: true, corporate: true, enterprise: true },
  { name: 'Bulk Applications', free: false, premium: false, agent: true, corporate: true, enterprise: true },
  { name: 'Sub-accounts', free: '0', premium: '0', agent: '5', corporate: '50', enterprise: 'Unlimited' },
  { name: 'API Access', free: false, premium: false, agent: false, corporate: 'Limited', enterprise: 'Full' },
  { name: 'Support', free: 'Email', premium: 'Priority', agent: 'Dedicated', corporate: 'Account Manager', enterprise: 'By agreement' },
];

// =====================================================
// COMPONENT
// =====================================================

const PricingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, token } = useAuth();
  const { showSuccess, showError } = useNotification();
  
  const [yearlyBilling, setYearlyBilling] = useState(true);
  const [loading, setLoading] = useState<string | null>(null);
  const [showComparison, setShowComparison] = useState(false);
  const [currentSubscription, setCurrentSubscription] = useState<any>(null);
  const [apiPrices, setApiPrices] = useState<Record<string, { price: number | null; yearlyPrice?: number | null }>>({});

  // Fetch current subscription + align prices with backend
  useEffect(() => {
    const fetchSubscription = async () => {
      if (!isAuthenticated) return;
      try {
        const res = await fetch(`${API_BASE_URL}/payments/subscription`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        if (data.success) {
          setCurrentSubscription(data.data);
        }
      } catch (err) {
        console.error('Failed to fetch subscription');
      }
    };
    fetchSubscription();

    fetch(`${API_BASE_URL}/tiers`)
      .then((r) => r.json())
      .then((body) => {
        if (!body?.success || !Array.isArray(body.data)) return;
        setApiPrices(Object.fromEntries(body.data.map((t: any) => [t.id, t])));
      })
      .catch(() => {});
  }, [isAuthenticated, token]);

  const displayTiers = TIERS.map((tier) => {
    const api = apiPrices[tier.id];
    if (!api) return tier;
    return {
      ...tier,
      monthlyPrice: api.price ?? tier.monthlyPrice,
      yearlyPrice: api.yearlyPrice ?? tier.yearlyPrice,
    };
  });

  // Handle tier selection
  const handleSelectTier = async (tierId: string) => {
    if (tierId === 'enterprise') {
      // Redirect to contact form
      window.location.href = `mailto:${EMAILS.SALES}?subject=Enterprise%20Inquiry`;
      return;
    }

    if (!isAuthenticated) {
      navigate(`/register?tier=${tierId}`);
      return;
    }

    if (tierId === 'free') {
      navigate('/dashboard');
      return;
    }

    setLoading(tierId);
    try {
      const res = await fetch(`${API_BASE_URL}/payments/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          tier: tierId,
          billingPeriod: yearlyBilling ? 'yearly' : 'monthly',
          email: user?.email,
          userId: user?.id
        })
      });

      const data = await res.json();
      if (data.success && data.data?.url) {
        window.location.href = data.data.url;
      } else if (data.code === 'already_entitled' || data.code === 'BILLED_BY_HUB' || res.status === 409) {
        showError(data.message || 'Visa access is included in your Hub plan. Visa will not charge again.');
      } else if (data.data?.mock && process.env.NODE_ENV !== 'production') {
        // Development mode only - simulate success
        showSuccess('Test checkout completed - tier activated');
        navigate('/dashboard');
      } else {
        throw new Error(data.message || 'Failed to create checkout session');
      }
    } catch (err: any) {
      showError(err.message);
    } finally {
      setLoading(null);
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #030B1B 0%, #0F172A 100%)',
      py: 8
    }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Chip
            icon={<LocalOffer />}
            label="Special Launch Pricing"
            sx={{ mb: 2, bgcolor: 'rgba(14,165,233,0.15)', color: '#0EA5E9', border: '1px solid rgba(14,165,233,0.3)', '& .MuiChip-icon': { color: '#0EA5E9' } }}
          />
          <Typography variant="h2" fontWeight={800} gutterBottom sx={{ color: 'white' }}>
            Choose Your Plan
          </Typography>
          <Typography variant="h6" sx={{ maxWidth: 600, mx: 'auto', mb: 4, color: 'rgba(255,255,255,0.55)' }}>
            {currentSubscription?.billedByHub
              ? 'Your Visa access is included in your Hub plan. You will not be charged again on Visa.'
              : 'Start free and scale as you grow. All plans include access to 249 countries and territories (ISO 3166-1) and our world-class visa intelligence platform.'}
          </Typography>
          {currentSubscription?.billedByHub && (
            <Chip
              label="Billed by Hub"
              sx={{ mb: 3, bgcolor: 'rgba(45,106,79,0.25)', color: '#d8f3dc', border: '1px solid #2d6a4f' }}
            />
          )}

          {/* Billing Toggle */}
          <Paper
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              p: 1,
              borderRadius: 3,
              bgcolor: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.06)',
              boxShadow: 'none'
            }}
          >
            <Typography
              component="div"
              sx={{
                px: 2,
                py: 1,
                borderRadius: 2,
                cursor: 'pointer',
                bgcolor: !yearlyBilling ? 'primary.main' : 'transparent',
                color: !yearlyBilling ? 'white' : 'rgba(255,255,255,0.55)',
                fontWeight: 600,
                transition: 'all 0.2s'
              }}
              onClick={() => setYearlyBilling(false)}
            >
              Monthly
            </Typography>
            <Typography
              component="div"
              sx={{
                px: 2,
                py: 1,
                borderRadius: 2,
                cursor: 'pointer',
                bgcolor: yearlyBilling ? 'primary.main' : 'transparent',
                color: yearlyBilling ? 'white' : 'rgba(255,255,255,0.55)',
                fontWeight: 600,
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
              onClick={() => setYearlyBilling(true)}
            >
              Yearly
              <Chip label="Save 30%" size="small" color="success" sx={{ height: 20 }} />
            </Typography>
          </Paper>
        </Box>

        {/* Pricing Cards */}
        <Grid container spacing={3} justifyContent="center" sx={{ mb: 8 }}>
          {displayTiers.slice(0, 4).map((tier, index) => (
            <Grid item xs={12} sm={6} lg={3} key={tier.id}>
              <Fade in timeout={300 + index * 100}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    borderRadius: 4,
                    border: tier.popular ? '2px solid' : '1px solid',
                    borderColor: tier.popular ? tier.color : 'rgba(255,255,255,0.06)',
                    bgcolor: tier.popular ? 'rgba(139,92,246,0.08)' : 'rgba(255,255,255,0.03)',
                    backdropFilter: 'blur(12px)',
                    transform: tier.popular ? 'scale(1.05)' : 'none',
                    boxShadow: tier.popular 
                      ? '0 20px 60px rgba(139, 92, 246, 0.25)'
                      : '0 4px 20px rgba(0,0,0,0.2)',
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: tier.popular ? 'scale(1.07)' : 'scale(1.02)',
                      boxShadow: '0 20px 60px rgba(0,0,0,0.4)'
                    }
                  }}
                >
                  {tier.popular && (
                    <Chip
                      icon={<Star />}
                      label="Most Popular"
                      sx={{
                        position: 'absolute',
                        top: -12,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        fontWeight: 700,
                        bgcolor: '#8B5CF6',
                        color: 'white',
                        '& .MuiChip-icon': { color: 'white' }
                      }}
                    />
                  )}

                  <CardContent sx={{ p: 4, flexGrow: 1 }}>
                    {/* Icon & Name */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar
                        sx={{
                          width: 48,
                          height: 48,
                          background: tier.gradient
                        }}
                      >
                        {tier.icon}
                      </Avatar>
                      <Box>
                        <Typography variant="h5" fontWeight={700} sx={{ color: 'white' }}>{tier.name}</Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.55)' }}>
                          {tier.description}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Price */}
                    <Box sx={{ my: 3 }}>
                      {tier.monthlyPrice !== null ? (
                        <>
                          <Typography
                            variant="h3"
                            fontWeight={800}
                            sx={{ display: 'inline', color: 'white' }}
                          >
                            ${yearlyBilling 
                              ? ((tier.yearlyPrice ?? 0) / 12).toFixed(2)
                              : (tier.monthlyPrice ?? 0).toFixed(2)
                            }
                          </Typography>
                          <Typography variant="body2" component="span" sx={{ color: 'rgba(255,255,255,0.55)' }}>
                            /month
                          </Typography>
                          {yearlyBilling && tier.savings && (
                            <Typography variant="body2" color="success.main" fontWeight={600} sx={{ mt: 1 }}>
                              {tier.savings}
                            </Typography>
                          )}
                        </>
                      ) : (
                        <Typography variant="h4" fontWeight={700} sx={{ color: 'white' }}>Custom</Typography>
                      )}
                    </Box>

                    <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.06)' }} />

                    {/* Features */}
                    <List dense>
                      {tier.features.map((feature, i) => (
                        <ListItem key={i} disablePadding sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            {feature.included ? (
                              <Check sx={{ color: '#10B981' }} />
                            ) : (
                              <Close sx={{ color: 'rgba(255,255,255,0.2)' }} />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={feature.text}
                            primaryTypographyProps={{
                              variant: 'body2',
                              sx: { color: feature.included ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.25)' }
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>

                  <CardActions sx={{ p: 3, pt: 0 }}>
                    <Button
                      fullWidth
                      variant={tier.popular ? 'contained' : 'outlined'}
                      size="large"
                      onClick={() => handleSelectTier(tier.id)}
                      disabled={
                        loading === tier.id ||
                        currentSubscription?.tier === tier.id ||
                        Boolean(currentSubscription?.billedByHub)
                      }
                      endIcon={loading === tier.id ? <CircularProgress size={16} /> : <ArrowForward />}
                      sx={{
                        py: 1.5,
                        borderRadius: 2,
                        fontWeight: 700,
                        ...(tier.popular ? {
                          background: 'linear-gradient(135deg, #0EA5E9 0%, #14B8A6 100%)',
                          color: 'white',
                          '&:hover': { background: 'linear-gradient(135deg, #0EA5E9 0%, #14B8A6 100%)', opacity: 0.9 }
                        } : {
                          borderColor: 'rgba(255,255,255,0.15)',
                          color: 'rgba(255,255,255,0.7)',
                          '&:hover': { borderColor: 'rgba(255,255,255,0.3)', bgcolor: 'rgba(255,255,255,0.05)' }
                        })
                      }}
                    >
                      {currentSubscription?.tier === tier.id ? 'Current Plan' : tier.cta}
                    </Button>
                  </CardActions>
                </Card>
              </Fade>
            </Grid>
          ))}
        </Grid>

        {/* Enterprise CTA */}
        <Paper
          sx={{
            p: 4,
            mb: 8,
            borderRadius: 4,
            background: 'linear-gradient(135deg, #1E3A8A 0%, #3B82F6 100%)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 3
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Avatar sx={{ width: 64, height: 64, bgcolor: 'rgba(255,255,255,0.2)' }}>
              <Apartment sx={{ fontSize: 32 }} />
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight={700}>Enterprise Solutions</Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Custom pricing, dedicated support, and unlimited everything for large organizations.
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            size="large"
            onClick={() => handleSelectTier('enterprise')}
            sx={{
              bgcolor: 'white',
              color: 'primary.main',
              fontWeight: 700,
              '&:hover': { bgcolor: 'grey.100' }
            }}
          >
            Contact Sales
          </Button>
        </Paper>

        {/* Feature Comparison */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="text"
            onClick={() => setShowComparison(!showComparison)}
            endIcon={showComparison ? '−' : '+'}
            sx={{ color: 'rgba(255,255,255,0.7)' }}
          >
            {showComparison ? 'Hide' : 'Show'} Full Feature Comparison
          </Button>
        </Box>

        {showComparison && (
          <Fade in>
            <Paper sx={{ p: 3, borderRadius: 3, overflow: 'auto', mb: 8, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: 16, borderBottom: '2px solid rgba(255,255,255,0.1)', color: 'white' }}>
                      Feature
                    </th>
                    {['free', 'premium', 'agent', 'corporate', 'enterprise'].map(tier => (
                      <th key={tier} style={{ textAlign: 'center', padding: 16, borderBottom: '2px solid rgba(255,255,255,0.1)', color: 'white' }}>
                        {tier.charAt(0).toUpperCase() + tier.slice(1)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {FEATURE_COMPARISON.map((row, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent' }}>
                      <td style={{ padding: 16, fontWeight: 500, color: 'rgba(255,255,255,0.7)' }}>{row.name}</td>
                      {['free', 'premium', 'agent', 'corporate', 'enterprise'].map(tier => (
                        <td key={tier} style={{ textAlign: 'center', padding: 16, color: 'rgba(255,255,255,0.55)' }}>
                          {typeof row[tier as keyof typeof row] === 'boolean' ? (
                            row[tier as keyof typeof row] ? (
                              <Check sx={{ color: 'success.main' }} />
                            ) : (
                              <Close sx={{ color: 'grey.400' }} />
                            )
                          ) : (
                            <Typography variant="body2">{row[tier as keyof typeof row]}</Typography>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </Paper>
          </Fade>
        )}

        {/* FAQ / Trust Signals */}
        <Grid container spacing={4} sx={{ mb: 8 }}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, height: '100%', borderRadius: 3, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <Security sx={{ fontSize: 48, color: '#0EA5E9', mb: 2 }} />
              <Typography variant="h6" fontWeight={700} gutterBottom sx={{ color: 'white' }}>Secure Payments</Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.55)' }}>
                All payments processed securely through Stripe. Your data is encrypted and protected.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, height: '100%', borderRadius: 3, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <Support sx={{ fontSize: 48, color: '#8B5CF6', mb: 2 }} />
              <Typography variant="h6" fontWeight={700} gutterBottom sx={{ color: 'white' }}>Cancel Anytime</Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.55)' }}>
                No long-term contracts. Cancel your subscription anytime with no questions asked.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, height: '100%', borderRadius: 3, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <Celebration sx={{ fontSize: 48, color: '#10B981', mb: 2 }} />
              <Typography variant="h6" fontWeight={700} gutterBottom sx={{ color: 'white' }}>Billing Support</Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.55)' }}>
                Contact support if you have a billing issue or need help with a refund request.
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Final CTA */}
        <Paper
          sx={{
            p: 6,
            borderRadius: 4,
            background: 'linear-gradient(135deg, #1E3A5F 0%, #0F2744 50%, #0A1929 100%)',
            border: '1px solid rgba(255,255,255,0.06)',
            color: 'white',
            textAlign: 'center'
          }}
        >
          <Typography variant="h4" fontWeight={800} gutterBottom sx={{ color: 'white' }}>
            Ready to simplify your visa journey?
          </Typography>
          <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.7)', mb: 4, maxWidth: 600, mx: 'auto' }}>
            Choose a plan for your Tripnovac Visa research, preparation, and account workflow needs.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => handleSelectTier('premium')}
            sx={{
              background: 'linear-gradient(135deg, #0EA5E9 0%, #14B8A6 100%)',
              color: 'white',
              fontWeight: 700,
              px: 6,
              py: 1.5,
              '&:hover': { background: 'linear-gradient(135deg, #0EA5E9 0%, #14B8A6 100%)', opacity: 0.9 }
            }}
            endIcon={<RocketLaunch />}
          >
            Get Started Now
          </Button>
        </Paper>
      </Container>
    </Box>
  );
};

export default PricingPage;
